import type { Card } from '@campfire/cards';
import type { Rummy500Event, Rummy500View } from '@campfire/game-rummy500';
import { ACE_EITHER_RUNS, meldKind } from '@campfire/melds';
import { cardFace, cardLabel, pickTray, pile } from '../cards.ts';
import { h } from '../dom.ts';
import { name, others, type GameUi } from '../games.ts';
import { applyOrder, moveBlock, type Move } from '../order.ts';

const SEL = 'r5-sel';
const SUIT_ORDER = 'SHDC';
const RANK_ORDER = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
const bySuit = (hand: readonly Card[]) =>
  [...hand].sort((a, b) => SUIT_ORDER.indexOf(a.suit ?? 'X') - SUIT_ORDER.indexOf(b.suit ?? 'X') || RANK_ORDER.indexOf(a.rank) - RANK_ORDER.indexOf(b.rank));
const byRank = (hand: readonly Card[]) =>
  [...hand].sort((a, b) => RANK_ORDER.indexOf(a.rank) - RANK_ORDER.indexOf(b.rank) || SUIT_ORDER.indexOf(a.suit ?? 'X') - SUIT_ORDER.indexOf(b.suit ?? 'X'));

export const rummy500Ui: GameUi = {
  id: 'rummy500',
  privateHands: true,
  seatInfo: (ctx, s) => {
    const v = ctx.msg.view as Rummy500View;
    const k = v.handCounts[s] ?? 0;
    return `${k} card${k === 1 ? '' : 's'} · ${v.scores[s] ?? 0} pts · +${v.tablePoints[s] ?? 0} on table`;
  },
  render(ctx) {
    const v = ctx.msg.view as Rummy500View;
    const me = ctx.mySeat;
    const raw = v.hand ?? [];
    const sortKey = `keep:r5-sort:${me}`;
    const orderKey = `keep:r5-order:${me}`;
    const sortBy = ctx.local.get<string>(sortKey) ?? 'suit';
    const hand = sortBy === 'custom' ? applyOrder(raw, ctx.local.get<string[]>(orderKey)) : sortBy === 'rank' ? byRank(raw) : bySuit(raw);
    const picked = (ctx.local.get<string[]>(SEL) ?? []).filter((id) => raw.some((c) => c.id === id));
    const pickedCards = hand.filter((c) => picked.includes(c.id));
    const toggle = (id: string) => ctx.local.set(SEL, picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);
    const send = (a: { type: string } & Record<string, unknown>) => {
      ctx.local.set(SEL, []);
      ctx.act(a);
    };
    const move = (m: Move) => {
      ctx.local.set(orderKey, moveBlock(hand.map((c) => c.id), picked, m));
      ctx.local.set(sortKey, 'custom');
    };
    const drawing = ctx.myTurn && v.phase === 'draw';
    const playing = ctx.myTurn && v.phase === 'play';
    const must = v.mustUse ? raw.find((c) => c.id === v.mustUse) : undefined;
    const needsMust = !!v.mustUse && !picked.includes(v.mustUse);
    const canMeld = playing && !needsMust && meldKind(pickedCards, ACE_EITHER_RUNS) !== null;
    const fitsMeld = (i: number) => {
      const m = v.melds[i];
      return !!m && playing && !needsMust && pickedCards.length > 0 && meldKind([...m.cards, ...pickedCards], ACE_EITHER_RUNS) === m.kind;
    };
    const ev = (e: Rummy500Event | null): string => {
      if (!e) return `Round ${v.round}. ${name(ctx, v.turn)} ${v.turn === ctx.you ? 'go' : 'goes'} first.`;
      switch (e.kind) {
        case 'draw':
          return `${name(ctx, e.seat)} drew from the stock.`;
        case 'take':
          return `${name(ctx, e.seat)} took ${e.cards.map(cardLabel).join(' ')} from the discards.`;
        case 'meld':
          return `${name(ctx, e.seat)} melded ${e.cards.map(cardLabel).join(' ')}.`;
        case 'layoff':
          return `${name(ctx, e.seat)} laid off ${e.cards.map(cardLabel).join(' ')}.`;
        case 'discard':
          return `${name(ctx, e.seat)} discarded ${cardLabel(e.card)}.`;
        case 'round':
          return `Round ${e.round} over — ${e.out === null ? 'the stock ran out' : `${name(ctx, e.out)} went out`}. ` + ctx.names.map((_, s) => `${name(ctx, s)} ${(e.net[s] ?? 0) >= 0 ? '+' : ''}${e.net[s] ?? 0}`).join(', ');
      }
    };
    const a = v.arrangement;
    return h(
      'div.r5-table',
      {},
      others(ctx, v.handCounts, (s) => ((v.tablePoints[s] ?? 0) > 0 ? h('div.muted', {}, `+${v.tablePoints[s]} on table`) : null)),
      h(
        'div.row.r5-piles',
        {},
        h(
          `${drawing ? 'button' : 'div'}.ocean`,
          { 'data-testid': 'r5-stock', onclick: drawing ? () => send({ type: 'draw' }) : undefined },
          pile(v.stockCount),
          h('div.muted', {}, drawing ? 'Draw' : 'Stock'),
        ),
        h(
          'div.r5-spread',
          { 'data-testid': 'r5-discard' },
          v.discard.length
            ? v.discard.map((c, i) => {
                const ok = drawing && v.takeable.includes(c.id);
                const above = v.discard.length - i - 1;
                const face = cardFace(c, {
                  testid: `r5-discard-${i}`,
                  selected: ok,
                  onclick: ok ? () => send({ type: 'take', card: c.id }) : undefined,
                });
                if (ok) face.setAttribute('title', above ? `Take ${cardLabel(c)} and the ${above} card${above === 1 ? '' : 's'} above it` : `Take ${cardLabel(c)}`);
                return face;
              })
            : h('div.card.empty'),
        ),
      ),
      drawing ? h('div.hint', { 'data-testid': 'hint' }, v.takeable.length ? 'Draw from the stock, or tap a lit discard to take it and everything above it.' : 'Draw from the stock.') : null,
      h(
        'div.r5-melds',
        { 'data-testid': 'r5-melds' },
        v.melds.length
          ? v.melds.map((m, i) => {
              const fit = fitsMeld(i);
              return h(
                `${fit ? 'button' : 'div'}.meld-group${fit ? '.fits' : ''}`,
                { 'data-testid': `r5-meld-${i}`, onclick: fit ? () => send({ type: 'layoff', cards: picked, meld: i }) : undefined },
                h('div.meld-cards', {}, m.cards.map((c) => cardFace(c, { small: true }))),
                h('div.meld-label', {}, fit ? 'Tap to lay off here' : [...new Set(m.by)].map((s) => name(ctx, s)).join(' + ')),
              );
            })
          : h('div.muted', {}, 'No melds yet'),
      ),
      h('div.event', { 'data-testid': 'event' }, ev(v.last)),
      must && playing ? h('div.hint', { 'data-testid': 'r5-must' }, `Meld the ${cardLabel(must)} you took first — on its own meld or laid off.`) : null,
      playing && !picked.length ? h('div.hint', {}, 'Pick cards to meld or lay off, then pick one to discard.') : null,
      picked.length ? pickTray(pickedCards, toggle, () => ctx.local.set(SEL, [])) : null,
      playing && picked.length
        ? h(
            'div.row.actions.wrap',
            { 'data-testid': 'r5-actions' },
            canMeld ? h('button.primary', { 'data-testid': 'r5-meld', onclick: () => send({ type: 'meld', cards: picked }) }, `Meld ${picked.length}`) : null,
            picked.length === 1 && !v.mustUse ? h('button', { 'data-testid': 'r5-discard-sel', onclick: () => send({ type: 'discard', card: picked[0] }) }, 'Discard') : null,
            !canMeld && !v.melds.some((_, i) => fitsMeld(i)) && picked.length > 1 ? h('span.muted', {}, needsMust ? `Include the ${cardLabel(must as Card)}.` : 'Not a set or run.') : null,
          )
        : null,
      picked.length && raw.length > 1
        ? h(
            'div.row.actions.mover',
            { 'data-testid': 'r5-move' },
            h('span.muted', {}, picked.length > 1 ? 'Move together:' : 'Move:'),
            ([['start', '⇤'], [-1, '◀'], [1, '▶'], ['end', '⇥']] as const).map(([m, label]) =>
              h('button', { 'data-testid': `r5-move-${m}`, 'aria-label': `Move ${m}`, onclick: () => move(m) }, label),
            ),
          )
        : null,
      v.hand ? h('div.hand', { 'data-testid': 'hand' }, hand.map((c) => cardFace(c, { picked: picked.includes(c.id), onclick: () => toggle(c.id) }))) : null,
      a && a.melds.length
        ? h(
            'div.r5-suggest',
            { 'data-testid': 'r5-suggest' },
            h('span.muted', {}, 'Melds in your hand:'),
            a.melds.map((m, i) =>
              h('button.link', { 'data-testid': `r5-suggest-${i}`, onclick: () => ctx.local.set(SEL, m.map((c) => c.id)) }, m.map(cardLabel).join(' ')),
            ),
          )
        : null,
      a
        ? h(
            'div.row.actions',
            {},
            h('span.badge', { 'data-testid': 'r5-deadwood' }, `Unmelded ${a.points}`),
            h('span.muted', {}, 'Sort:'),
            (['suit', 'rank', 'custom'] as const).map((k) =>
              h(
                `button.link${sortBy === k ? '.on' : ''}`,
                { 'data-testid': `r5-sort-${k}`, onclick: () => (k === 'custom' && sortBy !== 'custom' && ctx.local.set(orderKey, hand.map((c) => c.id)), ctx.local.set(sortKey, k)) },
                { suit: 'By suit', rank: 'By rank', custom: 'My order' }[k],
              ),
            ),
          )
        : null,
      h('div.muted', {}, `Round ${v.round} · first to ${v.target} · ${ctx.names.map((_, s) => `${name(ctx, s)} ${v.scores[s] ?? 0}`).join(' · ')}`),
    );
  },
};
